"use strict";
// packages/shared-schemas/src/websiteForms.ts
//
// Public website forms contract, version 1. The SDK (packages/website-forms-sdk)
// posts an envelope to POST /forms/v1/submit and reads GET /forms/v1/config.
// These schemas are what the endpoint validates; websiteForms.schema.json is
// the same contract as JSON Schema for third-party site builders, kept in
// sync by __tests__/websiteForms.test.ts.
//
// Every accepted submission answers the same body ({ ok: true, status:
// 'received' }) whether it created, merged or silently dropped, so the
// endpoint never tells a caller whether an address is known.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.2.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigResponseSchema = exports.PublicClassificationOptionsSchema = exports.ErrorResponseSchema = exports.WEBSITE_FORMS_ERROR_MESSAGES = exports.WEBSITE_FORMS_ERROR_CODES = exports.WEBSITE_FORMS_ERROR_STATUS = exports.SubmitResponseSchema = exports.WEBSITE_FORM_CONSENT_PURPOSES = exports.WEBSITE_FORM_DATA_SCHEMAS = exports.ReferralDataSchema = exports.ReferralClientSchema = exports.LOAN_AMOUNT_BAND_LABELS = exports.LoanAmountBandSchema = exports.LOAN_AMOUNT_BANDS = exports.NewsletterDataSchema = exports.EnquiryDataSchema = exports.EnquiryCustomFieldsSchema = exports.MAX_ENQUIRY_CUSTOM_FIELDS = exports.EnvelopeSchema = exports.AttributionSchema = exports.LastTouchSchema = exports.META_COOKIE_PATTERN = exports.ConsentGrantSchema = exports.WEBSITE_FORMS_PATHS = exports.WEBSITE_FORMS_CONTRACT_VERSION = void 0;
exports.websiteFormRecaptchaAction = websiteFormRecaptchaAction;
exports.parseWebsiteFormData = parseWebsiteFormData;
exports.missingRequiredConsents = missingRequiredConsents;
const zod_1 = require("zod");
const consent_1 = require("./consent");
const leadClassification_1 = require("./leadClassification");
const websiteConnection_1 = require("./websiteConnection");
const partnerNetwork_1 = require("./partnerNetwork");
exports.WEBSITE_FORMS_CONTRACT_VERSION = 1;
/** Paths served by the websiteForms onRequest function behind the API edge. */
exports.WEBSITE_FORMS_PATHS = Object.freeze({
    config: '/forms/v1/config',
    sdk: '/forms/v1/sdk.js',
    submit: '/forms/v1/submit',
});
/** reCAPTCHA Enterprise action the SDK executes and the server expects, per form. */
function websiteFormRecaptchaAction(form) {
    return `website_${form}`;
}
// ─── Envelope ───────────────────────────────────────────────────────────────
exports.ConsentGrantSchema = zod_1.z.object({
    purpose: consent_1.ConsentPurpose,
    versionId: zod_1.z.string().max(64),
    granted: zod_1.z.boolean(),
});
const attr = zod_1.z.string().max(200).optional();
/** A Meta `_fbc` / `_fbp` cookie value (`fb.<subdomain index>.<creation ms>.<id>`). */
exports.META_COOKIE_PATTERN = /^fb\.\d\.\d+\..+$/;
/** Last ad touch the SDK stored on this browser (opt-in). `at` is a bounded free string, never a datetime check. */
exports.LastTouchSchema = zod_1.z.object({
    utmSource: attr,
    utmMedium: attr,
    utmCampaign: attr,
    utmContent: attr,
    utmTerm: attr,
    gclid: attr,
    gbraid: attr,
    wbraid: attr,
    fbclid: attr,
    msclkid: attr,
    referrerOrigin: attr,
    landingPath: attr,
    at: zod_1.z.string().max(64).optional(),
});
exports.AttributionSchema = zod_1.z.object({
    utmSource: attr,
    utmMedium: attr,
    utmCampaign: attr,
    utmContent: attr,
    utmTerm: attr,
    gclid: attr,
    gbraid: attr,
    wbraid: attr,
    fbclid: attr,
    msclkid: attr,
    /** Meta `_fbc` / `_fbp` cookies; sent only when the site opts in (data-capture-ad-ids). */
    fbc: zod_1.z.string().max(500).optional(),
    fbp: zod_1.z.string().max(500).optional(),
    referrerOrigin: zod_1.z.string().max(300).optional(),
    landingPath: zod_1.z.string().max(500).optional(),
    sourcePath: zod_1.z.string().max(500),
    lastTouch: exports.LastTouchSchema.optional(),
});
exports.EnvelopeSchema = zod_1.z.object({
    v: zod_1.z.literal(exports.WEBSITE_FORMS_CONTRACT_VERSION),
    siteKey: zod_1.z.string().min(1).max(64),
    form: websiteConnection_1.WebsiteFormKind,
    /** Generated by the SDK per submission; a retry reuses it (idempotency). */
    submissionId: zod_1.z.string().uuid(),
    botToken: zod_1.z.string().max(4096),
    clientAt: zod_1.z.string().max(64).optional(),
    consents: zod_1.z.array(exports.ConsentGrantSchema).max(5),
    attribution: exports.AttributionSchema,
    /** Validated per form by WEBSITE_FORM_DATA_SCHEMAS after the site key resolves. */
    data: zod_1.z.unknown(),
});
// ─── Form data ──────────────────────────────────────────────────────────────
const name = zod_1.z.string().trim().min(1).max(100);
const email = zod_1.z.string().trim().toLowerCase().email().max(254);
const phone = zod_1.z.string().trim().min(1).max(50);
/** Custom fields a site adds to its enquiry form: at most 20, string values only. */
exports.MAX_ENQUIRY_CUSTOM_FIELDS = 20;
exports.EnquiryCustomFieldsSchema = zod_1.z
    .record(zod_1.z.string().max(1000))
    .refine((r) => Object.keys(r).length <= exports.MAX_ENQUIRY_CUSTOM_FIELDS, `At most ${exports.MAX_ENQUIRY_CUSTOM_FIELDS} custom fields`)
    .refine((r) => Object.keys(r).every((k) => k.length >= 1 && k.length <= 64), 'Custom field names must be 1 to 64 characters');
exports.EnquiryDataSchema = zod_1.z
    .object({
    firstName: name,
    lastName: name.optional(),
    email: email.optional(),
    phone: phone.optional(),
    message: zod_1.z.string().max(5000).optional(),
    classification: leadClassification_1.LeadClassificationInputSchema.optional(),
    requestedBrokerSlug: zod_1.z.string().max(80).optional(),
    customFields: exports.EnquiryCustomFieldsSchema.optional(),
    /** Key of a named enquiry form on the connection (resolveEnquiryTarget). Unknown keys fall back to the connection's enquiry settings. */
    variant: zod_1.z.string().max(40).optional(),
})
    .refine((d) => !!d.email || !!d.phone, { message: 'Provide an email or a phone number', path: ['email'] });
exports.NewsletterDataSchema = zod_1.z.object({
    email,
    firstName: name.optional(),
});
exports.LOAN_AMOUNT_BANDS = ['under_500k', '500k_1m', '1m_2m', '2m_plus', 'unsure'];
exports.LoanAmountBandSchema = zod_1.z.enum(exports.LOAN_AMOUNT_BANDS);
exports.LOAN_AMOUNT_BAND_LABELS = {
    under_500k: 'Under $500k',
    '500k_1m': '$500k to $1m',
    '1m_2m': '$1m to $2m',
    '2m_plus': '$2m or more',
    unsure: 'Not sure yet',
};
exports.ReferralClientSchema = zod_1.z
    .object({
    firstName: name,
    lastName: name.optional(),
    email: email.optional(),
    phone: phone.optional(),
})
    .refine((c) => !!c.email || !!c.phone, { message: "Provide the client's email or phone number", path: ['email'] });
exports.ReferralDataSchema = zod_1.z.object({
    referrer: zod_1.z.object({
        firstName: name,
        lastName: name.optional(),
        email,
        phone: phone.optional(),
        business: zod_1.z.string().trim().min(1).max(200),
    }),
    client: exports.ReferralClientSchema,
    loanAmountBand: exports.LoanAmountBandSchema.optional(),
    /**
     * The same five answers an enquiry collects, loan amount band included,
     * checked against the tenant's options for the referral form's service
     * category. Unknown values are dropped and logged, never fail the referral.
     */
    classification: leadClassification_1.LeadClassificationInputSchema.optional(),
    /**
     * Owner-occupied, investment or commercial. Stored as the visitor's stated
     * type, a hint for the desk; fees never read it. The desk confirms the
     * actual type before pricing.
     */
    propertyType: partnerNetwork_1.PropertyTypeSchema.optional(),
    notes: zod_1.z.string().max(2000).optional(),
});
exports.WEBSITE_FORM_DATA_SCHEMAS = {
    enquiry: exports.EnquiryDataSchema,
    newsletter: exports.NewsletterDataSchema,
    referral: exports.ReferralDataSchema,
};
/** Validate an envelope's `data` for its form. */
function parseWebsiteFormData(form, data) {
    const r = exports.WEBSITE_FORM_DATA_SCHEMAS[form].safeParse(data);
    return r.success ? { success: true, data: r.data } : { success: false, error: r.error };
}
/**
 * Consent purposes each form presents, and whether the form is refused
 * without a grant. A required purpose with no active text for the company
 * means the form cannot be offered (config omits it).
 */
exports.WEBSITE_FORM_CONSENT_PURPOSES = {
    enquiry: [
        { purpose: 'enquiry_contact', required: true },
        { purpose: 'marketing_email', required: false },
    ],
    newsletter: [{ purpose: 'marketing_email', required: true }],
    referral: [
        { purpose: 'referral_third_party', required: true },
        { purpose: 'referrer_terms', required: true },
    ],
};
/** Required purposes with no granted consent in the envelope. Empty = consent requirements met. */
function missingRequiredConsents(form, consents) {
    return exports.WEBSITE_FORM_CONSENT_PURPOSES[form]
        .filter((p) => p.required && !consents.some((c) => c.purpose === p.purpose && c.granted === true))
        .map((p) => p.purpose);
}
// ─── Responses ──────────────────────────────────────────────────────────────
exports.SubmitResponseSchema = zod_1.z.object({
    ok: zod_1.z.literal(true),
    status: zod_1.z.enum(['received']),
    reference: zod_1.z.string().optional(),
});
/** Error code -> HTTP status. The body is { ok: false, code, message }. */
exports.WEBSITE_FORMS_ERROR_STATUS = Object.freeze({
    invalid: 400,
    consent_version_invalid: 400,
    rejected: 403,
    unknown_site: 404,
    form_disabled: 409,
    rate_limited: 429,
    unavailable: 503,
});
exports.WEBSITE_FORMS_ERROR_CODES = Object.keys(exports.WEBSITE_FORMS_ERROR_STATUS);
/** Plain English messages, safe to show a site visitor. */
exports.WEBSITE_FORMS_ERROR_MESSAGES = {
    invalid: 'Some details are missing or not valid. Please check the form and try again.',
    consent_version_invalid: 'This form is out of date. Please refresh the page and try again.',
    rejected: 'We could not accept this submission. Please try again or contact us directly.',
    unknown_site: 'This form is not connected. Please contact us directly.',
    form_disabled: 'This form is not taking submissions right now. Please contact us directly.',
    rate_limited: 'Too many attempts. Please wait a few minutes and try again.',
    unavailable: 'Something went wrong on our side. Please try again shortly.',
};
exports.ErrorResponseSchema = zod_1.z.object({
    ok: zod_1.z.literal(false),
    code: zod_1.z.enum(exports.WEBSITE_FORMS_ERROR_CODES),
    message: zod_1.z.string().max(500).optional(),
});
const PublicOptionSchema = zod_1.z.object({ id: zod_1.z.string(), label: zod_1.z.string() });
/** Active options per classification dimension, as the SDK renders them. Null = the category asks nothing. */
exports.PublicClassificationOptionsSchema = zod_1.z
    .object({
    loanPurpose: zod_1.z.array(PublicOptionSchema),
    propertyUse: zod_1.z.array(PublicOptionSchema),
    borrowerSituation: zod_1.z.array(PublicOptionSchema),
    loanStructure: zod_1.z.array(PublicOptionSchema),
    loanAmount: zod_1.z.array(PublicOptionSchema),
})
    .nullable();
exports.ConfigResponseSchema = zod_1.z.object({
    v: zod_1.z.literal(exports.WEBSITE_FORMS_CONTRACT_VERSION),
    connectionLabel: zod_1.z.string(),
    forms: zod_1.z.object({ enquiry: zod_1.z.boolean(), newsletter: zod_1.z.boolean(), referral: zod_1.z.boolean() }),
    consentTexts: zod_1.z.array(zod_1.z.object({
        purpose: consent_1.ConsentPurpose,
        versionId: zod_1.z.string(),
        text: zod_1.z.string(),
        required: zod_1.z.boolean(),
    })),
    recaptchaSiteKey: zod_1.z.string(),
    classificationOptions: exports.PublicClassificationOptionsSchema,
    /** Named enquiry forms; send `data.variant` = key. Each carries its own category's options. */
    enquiryVariants: zod_1.z
        .array(zod_1.z.object({ key: zod_1.z.string(), label: zod_1.z.string(), classificationOptions: exports.PublicClassificationOptionsSchema }))
        .optional(),
    /** Options the referral form asks, for its service category. */
    referralClassificationOptions: exports.PublicClassificationOptionsSchema.optional(),
});
const _CONFIG_DIMENSIONS_MATCH = true;
void _CONFIG_DIMENSIONS_MATCH;
